import { solutionsData } from '@/mocks/solutions';
import { SITE_ORIGIN, buildCanonicalUrl, getPageMeta, normalizePath } from '@/lib/seo';

const ORG_ID = `${SITE_ORIGIN}/#organization`;

export interface FaqEntry {
  q: string;
  a: string;
}

interface ServiceEntry {
  name: string;
  description: string;
  serviceType: string;
}

// FAQ por rota. O conteúdo espelha exatamente o que está visível na página
// (componente FaqAccordion) — exigência do Google para o FAQPage.
export const FAQ_BY_PATH: Record<string, FaqEntry[]> = {
  '/pgr-pcmsco': [
    {
      q: 'Minha empresa precisa de PGR mesmo sendo pequena?',
      a: 'Sim. O PGR é obrigatório para empresas de todos os portes com empregados regidos pela CLT, independentemente do grau de risco. O que muda é a complexidade do documento.',
    },
    {
      q: 'Quanto tempo leva?',
      a: 'Depende do porte e da complexidade, mas a maioria das empresas consegue regularizar a documentação em poucas semanas com nosso plano de ação.',
    },
    {
      q: 'O que acontece se eu não tiver PGR/PCMSO?',
      a: 'Você fica exposto a multas, autuações e passivo trabalhista em caso de fiscalização, além de não ter a documentação necessária para eventuais obrigações legais.',
    },
  ],
  '/gestao-afastados-absenteismo': [
    {
      q: 'Como vocês acompanham os afastamentos?',
      a: 'Acompanhamos cada caso de perto, desde o atestado até o retorno ao trabalho, registrando tudo em uma plataforma online e identificando as causas recorrentes.',
    },
    {
      q: 'Isso substitui meu RH?',
      a: 'Não. Tiramos o trabalho operacional de gestão de afastamentos do RH, mas a decisão e o relacionamento com a equipe continuam com a sua empresa.',
    },
    {
      q: 'Em quanto tempo eu vejo resultado?',
      a: 'O diagnóstico inicial já traz clareza sobre as causas. A redução mensurável aparece ao longo dos primeiros meses, com metas acompanhadas em conjunto.',
    },
  ],
  '/saude-mental-corporativa': [
    {
      q: 'É confidencial mesmo para o RH?',
      a: 'Sim. O conteúdo das sessões é sigiloso entre colaborador e psicólogo. O RH acompanha apenas indicadores gerais de uso, nunca o que foi tratado.',
    },
    {
      q: 'Como os colaboradores acessam?',
      a: 'Por uma plataforma online, com agendamento simples e sessões por videochamada. Sem deslocamento e sem burocracia.',
    },
    {
      q: 'Isso conta para o PGR/NR-1?',
      a: 'Sim. O programa apoia o tratamento dos riscos psicossociais previstos na NR-1 e se integra à gestão de SST da Nexus.',
    },
  ],
  '/terceirizacao-sst': [
    {
      q: 'Como funciona a terceirização da SST?',
      a: 'Você centraliza toda a gestão de saúde e segurança do trabalho na Nexus. Nós cuidamos de PGR, PCMSO, exames, afastamentos e demais frentes em uma única plataforma.',
    },
    {
      q: 'Preciso migrar tudo de uma vez?',
      a: 'Não. Podemos fazer a transição de forma gradual, priorizando o que é mais urgente para a sua operação e mantendo a continuidade dos processos.',
    },
    {
      q: 'A plataforma substitui meu RH?',
      a: 'Não. Somos um parceiro que dá visibilidade e tira o trabalho operacional do RH, mas a decisão e o relacionamento com a equipe continuam com a sua empresa.',
    },
  ],
};

// Serviço declarado por rota. Os nomes vêm do conteúdo real da página.
const SERVICE_BY_PATH: Record<string, ServiceEntry> = {
  '/pgr-pcmsco': {
    name: 'PGR e PCMSO',
    description:
      'Elaboração e gestão do Programa de Gerenciamento de Riscos (PGR) e do Programa de Controle Médico de Saúde Ocupacional (PCMSO) para empresas.',
    serviceType: 'Saúde e Segurança do Trabalho',
  },
  '/gestao-afastados-absenteismo': {
    name: 'Gestão de Afastamentos e Absenteísmo',
    description:
      'Gestão de afastamentos e redução de absenteísmo com acompanhamento de cada caso, da abertura até o retorno ao trabalho.',
    serviceType: 'Saúde Ocupacional',
  },
  '/terceirizacao-sst': {
    name: 'Gestão de SST Terceirizada',
    description:
      'Gestão completa de Saúde e Segurança do Trabalho terceirizada, centralizando PGR, PCMSO, exames, afastamentos e treinamentos.',
    serviceType: 'Saúde e Segurança do Trabalho',
  },
  '/saude-mental-corporativa': {
    name: 'Programa de Saúde Mental Corporativa',
    description:
      'Programa de saúde mental corporativa com psicoterapia online, sigilo garantido e indicadores para o RH.',
    serviceType: 'Saúde Mental no Trabalho',
  },
  '/agendamento-exames-ocupacionais': {
    name: 'Agendamento de Exames Ocupacionais e ASO',
    description:
      'Agendamento de exames ocupacionais e emissão de ASO com rede credenciada e gestão de exames periódicos.',
    serviceType: 'Medicina Ocupacional',
  },
};

const resolveService = (cleanPath: string): ServiceEntry | null => {
  const direct = SERVICE_BY_PATH[cleanPath];
  if (direct) return direct;

  if (cleanPath.startsWith('/solucoes/')) {
    const slug = cleanPath.replace('/solucoes/', '');
    const data = solutionsData[slug];
    if (data) {
      return {
        name: data.title,
        description: data.subtitle,
        serviceType: 'Saúde e Segurança do Trabalho',
      };
    }
  }
  return null;
};

const buildBreadcrumb = (cleanPath: string, url: string) => {
  const pageLabel = getPageMeta(cleanPath).title.split('|')[0].trim();
  const items: Record<string, unknown>[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_ORIGIN}/`,
    },
  ];

  if (cleanPath.startsWith('/solucoes/')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Soluções',
      item: `${SITE_ORIGIN}/`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: pageLabel,
      item: url,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: pageLabel,
      item: url,
    });
  }

  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: items,
  };
};

/**
 * Monta o grafo JSON-LD específico da rota atual (Service, FAQPage e Breadcrumb).
 * A entidade Organization/MedicalBusiness fica estática no index.html.
 */
export const buildStructuredData = (pathname: string): object | null => {
  const cleanPath = normalizePath(pathname);
  const url = buildCanonicalUrl(cleanPath);
  const graph: Record<string, unknown>[] = [];

  const service = resolveService(cleanPath);
  if (service) {
    graph.push({
      '@type': 'Service',
      '@id': `${url}#service`,
      name: service.name,
      description: service.description,
      serviceType: service.serviceType,
      provider: { '@id': ORG_ID },
      areaServed: { '@type': 'Country', name: 'Brasil' },
      audience: { '@type': 'BusinessAudience', name: 'Grandes empresas' },
      url,
    });
  }

  const faq = FAQ_BY_PATH[cleanPath];
  if (faq && faq.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    });
  }

  if (cleanPath !== '/' && getPageMeta(cleanPath) !== getPageMeta('/')) {
    graph.push(buildBreadcrumb(cleanPath, url));
  }

  if (graph.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};

// Schema de matéria (Article) para as páginas do blog.
export const buildArticleSchema = (params: {
  slug: string;
  titulo: string;
  resumo: string;
  imagem?: string;
  categoria?: string;
  publicadoEm?: string;
}): object => {
  const url = `${SITE_ORIGIN}/novidades-nexus/${params.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: params.titulo,
    description: params.resumo,
    image: params.imagem,
    articleSection: params.categoria,
    datePublished: params.publicadoEm,
    dateModified: params.publicadoEm,
    inLanguage: 'pt-BR',
    mainEntityOfPage: { '@id': url },
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
  };
};